import { lazy } from "react";
import { Routes } from "react-router";
import { Outlet, Route } from "react-router-dom";
import AuthGuard from "~Common/AuthGuard/AuthGuard.tsx";
import { PermissionNames } from "~enums/permissions";
import { AccessDeniedPage, NotFoundPage, ScopeErrorPage } from "~pages/ErrorPages";
import OrganizationTabsRoutes from "./OrganizationTabsRoutes";
import SeasonTabsRoutes from "./SeasonTabsRoutes.tsx";

const CompetitionSeasonList = lazy(() => import("~pages/Seasons/SeasonListPage/SeasonListPage"));
const DivisionsList = lazy(() => import("../Features/Conferences/DivisionsList"));
const LeaderCriteriaRuleListPage = lazy(() => import("~pages/LeaderCriteria/LeaderCriteriaRuleListPage/LeaderCriteriaRuleListPage"));
const ManageLeague = lazy(() => import("../Features/Leagues/ManageLeague"));
const ManageCompetition = lazy(() => import("~pages/Competitions/CompetitionsEditPage/ManageCompetition"));
const ManageClub = lazy(() => import("../Features/Club/ManageClub"));
const ManageConferences = lazy(() => import("../Features/Conferences/ManageConferences"));
const ManageDivision = lazy(() => import("../Features/Conferences/ManageDivision"));
const LeaderCriteriaEditPage = lazy(() => import("~pages/LeaderCriteria/LeaderCriteriaEditPage/LeaderCriteriaEditPage"));
const LeaderCriteriaRuleEditPage = lazy(() => import("~pages/LeaderCriteria/LeaderCriteriaRuleEditPage/LeaderCriteriaRuleEditPage"));
const ManageMatchProfile = lazy(() => import("../Features/MatchProfiles/ManageMatchProfile"));
const PersonProfile = lazy(() => import("~pages/Persons/PersonProfile/PersonProfile"));
const NewSeason = lazy(() => import("../Features/EditSeason/NewSeason/NewSeason"));
const ManageStandingConfigurations = lazy(() => import("../Features/StandingsConfigurations/ManageStandingConfiguration"));
const ManageVenue = lazy(() => import("../Features/Venues/ManageVenue"));
const ManageSite = lazy(() => import("../Features/Sites/ManageSite"));
const ManageTeam = lazy(() => import("../Features/Team/ManageTeam"));
const PermissionUserDetailsPage = lazy(() => import("~pages/Users/PermissionUserDetailsPage/PermissionUserDetailsPage"));
const PermissionUserEditPage = lazy(() => import("~pages/Users/PermissionUserEditPage/PermissionUserEditPage"));

const FixtureEditPage = lazy(() => import("../pages/Fixtures/FixtureEditPage"));
const BroadcastPage = lazy(() => import("../pages/Fixtures/BroadcastPage/BroadcastPage.tsx"));
const SeasonRosterListPage = lazy(() => import("~pages/Roster/SeasonRosterListPage/SeasonRosterListPage"));
const FixtureRosterListPage = lazy(() => import("~pages/Roster/FixtureRosterListPage/FixtureRosterListPage"));
const RosterImages = lazy(() => import("../Features/Roster/RosterImages/RosterImages"));
const Staff = lazy(() => import("../Features/Staff/Staff"));
const EditStandings = lazy(() => import("../Features/Standings/EditStandings/EditStandings"));
const EditStandingsAdjustments = lazy(() => import("../Features/StandingsAdjustments/EditStandingsAdjustments"));
const StandingsAdjustments = lazy(() => import("../Features/StandingsAdjustments/StandingsAdjustments"));
const SeasonEntityEditPage = lazy(() => import("~pages/SeasonEntities/SeasonEntityEditPage/SeasonEntityEditPage"));
const SeasonStageEditPage = lazy(() => import("~pages/SeasonStages/SeasonStageEditPage/SeasonStageEditPage"));
const SeasonPoolEditPage = lazy(() => import("~pages/SeasonPools/SeasonPoolEditPage/SeasonPoolEditPage"));
const NotificationsPage = lazy(() => import("../pages/Notifications/NotificationsPage.tsx"));
const CurrentUserDetailsPage = lazy(() => import("../pages/Users/CurrentUserDetailsPage/CurrentUserDetailsPage.tsx"));
const SelectCustomerPage = lazy(() => import("~pages/StartupWizard/SelectCustomerPage"));
const SelectOrganizationPage = lazy(() => import("~pages/StartupWizard/SelectOrganizationPage"));

const ApplicationRoutes = () => (
  <Routes>
    <Route index element={<SelectCustomerPage />} />
    <Route path="403" element={<AccessDeniedPage />} />
    <Route path=":customerId" element={<Outlet />}>
      <Route path="select/organization" element={<SelectOrganizationPage />} />
      <Route path="notifications" element={<NotificationsPage />} />

      <Route path="admin/:sport/:organizationId/user-details" element={<CurrentUserDetailsPage />} />
      <Route path="admin/:sport/:organizationId/403" element={<AccessDeniedPage />} />
      <Route path="admin/:sport/:organizationId/scope-error" element={<ScopeErrorPage />} />
      {/*ADMIN ROUTES: ORG LEVEL*/}
      <Route
        path="admin/:sport/:organizationId/leagues/manage/:dataId?"
        element={
          <AuthGuard permissionKeys={[PermissionNames.LEAGUE]}>
            <ManageLeague />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/competition/manage/:competitionId?"
        element={
          <AuthGuard permissionKeys={[PermissionNames.COMPETITION]}>
            <ManageCompetition />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/entity-groups/manage/:clubId?"
        element={
          <AuthGuard permissionKeys={[PermissionNames.ENTITY_GROUP]}>
            <ManageClub />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/conferences/manage/:dataId?"
        element={
          <AuthGuard permissionKeys={[PermissionNames.CONFERENCE]}>
            <ManageConferences />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/entities/manage/:entityId?"
        element={
          <AuthGuard permissionKeys={[PermissionNames.ENTITY]}>
            <ManageTeam />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/sites/manage/:dataId?"
        element={
          <AuthGuard permissionKeys={[PermissionNames.SITE]}>
            <ManageSite />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/venues/manage/:dataId?"
        element={
          <AuthGuard permissionKeys={[PermissionNames.VENUE]}>
            <ManageVenue />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/persons/manage/:personId?"
        element={
          <AuthGuard permissionKeys={[PermissionNames.PERSON]}>
            <PersonProfile />
          </AuthGuard>
        }
      />
      <Route
        path={"admin/:sport/:organizationId/standings-configuration/manage/:standingConfigurationId?"}
        element={
          <AuthGuard permissionKeys={[PermissionNames.STANDING_CONFIGURATION]}>
            <ManageStandingConfigurations />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/leaders-qualification/manage/:leaderCriteriaId?"
        element={
          <AuthGuard permissionKeys={[PermissionNames.LEADER_QUALIFIER]}>
            <LeaderCriteriaEditPage />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/match-profiles/manage/:matchProfileId?"
        element={
          <AuthGuard permissionKeys={[PermissionNames.MATCH_PROFILE]}>
            <ManageMatchProfile />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/leaders-qualification/:leaderCriteriaId/qualifiers"
        element={
          <AuthGuard permissionKeys={[PermissionNames.LEADER_CRITERIA_SET]}>
            <LeaderCriteriaRuleListPage />
          </AuthGuard>
        }
      />
      <Route
        path={"admin/:sport/:organizationId/leaders-qualification/:leaderCriteriaId/qualifiers/manage"}
        element={
          <AuthGuard permissionKeys={[PermissionNames.LEADER_CRITERIA_SET]}>
            <LeaderCriteriaRuleEditPage />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/conferences/:conferenceId/divisions"
        element={
          <AuthGuard permissionKeys={[PermissionNames.DIVISION]}>
            <DivisionsList />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/conferences/:conferenceId/divisions/manage/:divisionId?"
        element={
          <AuthGuard permissionKeys={[PermissionNames.DIVISION]}>
            <ManageDivision />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/permissions/manage/:userId"
        element={
          <AuthGuard permissionKeys={[PermissionNames.PERMISSIONS]}>
            <PermissionUserDetailsPage />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/permissions/manage/:userId/permission/:permissionId?"
        element={
          <AuthGuard permissionKeys={[PermissionNames.PERMISSIONS]}>
            <PermissionUserEditPage />
          </AuthGuard>
        }
      />
      {/* SEASON LEVEL ROUTES */}

      {/* Season level tabs */}

      <Route
        path="admin/:sport/:organizationId/competition/:competitionId/seasons/"
        element={
          <AuthGuard permissionKeys={[PermissionNames.COMPETITION]}>
            <CompetitionSeasonList />
          </AuthGuard>
        }
      />

      <Route
        path="admin/:sport/:organizationId/competition/:competitionId/seasons/add"
        element={
          <AuthGuard permissionKeys={[PermissionNames.SEASON]}>
            <NewSeason />
          </AuthGuard>
        }
      />

      <Route
        path="admin/:sport/:organizationId/seasons/:seasonId/entities/:entityId"
        element={
          <AuthGuard permissionKeys={[PermissionNames.SEASON_ENTITY]}>
            <SeasonEntityEditPage />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/seasons/:seasonId/stages/:stageCode"
        element={
          <AuthGuard permissionKeys={[PermissionNames.STAGE]}>
            <SeasonStageEditPage />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/seasons/:seasonId/pools/:poolCode"
        element={
          <AuthGuard permissionKeys={[PermissionNames.POOL]}>
            <SeasonPoolEditPage />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/seasons/:seasonId/entities/:entityId/roster"
        element={
          <AuthGuard permissionKeys={[PermissionNames.SEASON_ROSTER]}>
            <SeasonRosterListPage />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/seasons/:seasonId/fixtures/:fixtureId/roster"
        element={
          <AuthGuard permissionKeys={[PermissionNames.FIXTURE_ROSTER]}>
            <FixtureRosterListPage />
          </AuthGuard>
        }
      />

      <Route
        path="admin/:sport/:organizationId/seasons/:seasonId/entities/:entityId/roster/images"
        element={
          <AuthGuard permissionKeys={[PermissionNames.SEASON_ROSTER]}>
            <RosterImages />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/seasons/:seasonId/entities/:entityId/staff"
        element={
          <AuthGuard permissionKeys={[PermissionNames.ROLES]}>
            <Staff />
          </AuthGuard>
        }
      />

      <Route
        path="admin/:sport/:organizationId/standings/:standingId"
        element={
          <AuthGuard permissionKeys={[PermissionNames.STANDING]}>
            <EditStandings />
          </AuthGuard>
        }
      />

      <Route
        path="admin/:sport/:organizationId/seasons/:seasonId/standings/adjustments"
        element={
          <AuthGuard permissionKeys={[PermissionNames.STANDING_ADJUSTMENT]}>
            <StandingsAdjustments />
          </AuthGuard>
        }
      />
      <Route
        path={"admin/:sport/:organizationId/seasons/:seasonId/standings/adjustments/:standingsAdjustmentId"}
        element={
          <AuthGuard permissionKeys={[PermissionNames.STANDING_ADJUSTMENT]}>
            <EditStandingsAdjustments />
          </AuthGuard>
        }
      />

      <Route
        path="admin/:sport/:organizationId/seasons/:seasonId/fixtures/:fixtureId"
        element={
          <AuthGuard permissionKeys={[PermissionNames.FIXTURE]}>
            <FixtureEditPage />
          </AuthGuard>
        }
      />
      <Route
        path="admin/:sport/:organizationId/seasons/:seasonId/fixtures/:fixtureId/broadcast"
        element={
          <AuthGuard permissionKeys={[PermissionNames.FIXTURE]}>
            <BroadcastPage />
          </AuthGuard>
        }
      />

      <Route path="admin/:sport/:organizationId/*" element={<OrganizationTabsRoutes />} />
      <Route path="admin/:sport/:organizationId/seasons/:seasonId/*" element={<SeasonTabsRoutes />} />
    </Route>

    <Route path="*" element={<NotFoundPage />} />
  </Routes>
);

export default ApplicationRoutes;
